import { useState, useEffect, useRef  } from "react";
import { useNavigate } from "react-router-dom";
import { CONTRACT_STATUSES, } from "../data/data";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";

import { getAllProjects, createProject, updateProject, searchPartners, createPartner } from "../service/rpc";
import { useIsMobile } from "../hooks/useIsMobile";
import { useUser } from "../context/UserContext";
import toast from "react-hot-toast";

const ITEMS_PER_PAGE = 6;

const EMPTY_FORM = { name: "", customerName: "", partner_id: "", budget: "", endDate: "", startDate: "", description: "", status: "Active" };

const STATUS_MAP = {
  'in_progress': 'Active',
  'draft': 'Draft',
  'done': 'Completed',
  'cancelled': 'Cancelled'
};

const STATUS_REVERSE_MAP = {
  'Active': 'in_progress',
  'Draft': 'draft',
  'Completed': 'done',
  'Cancelled': 'cancelled'
};


const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};


export default function ContractsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();
  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");
  const [contractsData, setContractsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Inline state
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [totalItems, setTotalItems] = useState(0);
  const [saving, setSaving] = useState(false);

  // Auto-save refs
  const saveTimeout = useRef(null);
  const lastSavedForm = useRef(null);
  const { partnerId, loading: userLoading, uid, name: userName } = useUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;

        let domain = [];

        if (search.trim()) {
          const q = search.trim();
          const searchDomain = ['|', ['name', 'ilike', q], ['partner_id', 'ilike', q]];
          domain = domain.length > 0 ? ['&', ...domain, ...searchDomain] : searchDomain;
        }



        const { projects, count } = await getAllProjects(domain, 100, offset);

        const formatted = projects.map(p => ({
          id: p.id,
          name: p.name || "Unnamed Project",
          client: p.partner_id && p.partner_id.length > 1 ? p.partner_id[1] : "N/A",
          partner_id_raw: p.partner_id,
          status: STATUS_MAP[p.state] || "Draft",
          startDate: p.date_start && typeof p.date_start === 'string' ? p.date_start.split(' ')[0] : (p.date_start || "N/A"),
          endDate: p.date_end && typeof p.date_end === 'string' ? p.date_end.split(' ')[0] : (p.date_end || "N/A"),
          description: stripHtml(p.description) || "No description",
          budget: p.budget || 0,
        }));
        setContractsData(formatted);
        setTotalItems(count);
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading) {
      fetchData();
    }
  }, [search, currentPage, statusFilter, partnerId, uid, userLoading]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    const initial = { ...EMPTY_FORM, customerName: userName || "", partner_id: partnerId || "", startDate: today, endDate: "" };
    setForm(initial);
    lastSavedForm.current = initial;
    setIsCreating(true);
    setEditingId(null);
  };

  const [partnerSuggestions, setPartnerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const fetchPartners = async (q) => {
    try {
      const results = await searchPartners(q);
      setPartnerSuggestions(results);
      setShowSuggestions(true);
    } catch (e) {
      console.error(e);
    }
  };

  const openEdit = (e, contract) => {
    e.stopPropagation();
    const initial = {
      name: contract.name,
      budget: String(contract.budget || 0),
      startDate: contract.startDate === "N/A" ? "" : contract.startDate,
      endDate: contract.endDate === "N/A" ? "" : contract.endDate,
      description: contract.description || "",
      status: contract.status || "Draft",
    };
    setForm(initial);
    lastSavedForm.current = initial;
    setEditingId(contract.id);
    setIsCreating(false);
  };

  const cancelEdit = () => {
    setIsCreating(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const isDirty = JSON.stringify(form) !== JSON.stringify(lastSavedForm.current);
    if (!isDirty && !isCreating) return;

    try {
      setSaving(true);

      const payload = {
        name: form.name,
        partner_id: form.partner_id ? parseInt(form.partner_id) : (partnerId ? parseInt(partnerId) : false),
        date_start: (form.startDate && form.startDate !== "N/A") ? form.startDate : false,
        date_end: (form.endDate && form.endDate !== "N/A") ? form.endDate : false,
        user_id: parseInt(uid),
        description: form.description,
        state: STATUS_REVERSE_MAP[form.status] || 'draft',
        budget: parseFloat(form.budget) || 0,
      };

      if (isCreating) {
        const newId = await createProject(payload);
        setContractsData(prev => [{
          id: newId,
          name: form.name,
          client: userName || "Your Profile",
          partner_id_raw: partnerId ? [partnerId, userName] : null,
          budget: parseFloat(form.budget) || 0,
          status: form.status,
          startDate: form.startDate || "N/A",
          endDate: form.endDate || "N/A",
          description: form.description,
        }, ...prev]);
        setIsCreating(false);
      } else {
        await updateProject(editingId, payload);
        setContractsData(prev => prev.map(c => c.id === editingId
          ? {
            ...c,
            name: form.name,
            budget: parseFloat(form.budget) || 0,
            status: form.status,
            startDate: form.startDate || c.startDate,
            endDate: form.endDate || c.endDate,
            description: form.description,
          }
          : c
        ));
      }

      lastSavedForm.current = { ...form };
      toast.success(isCreating ? "Logged!" : "Saved!");
      cancelEdit();
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const pagedData = contractsData;

  return (
    <main className="main">


      <div className="page-header">
        <h1 className="page-header__title">Contracts</h1>
      </div>

      <div className="task-tab-header">
        <div className="page-header__actions">
          <button className="btn-create" onClick={openCreate}>+  New</button>
        </div>
        <SearchBar
          search={search}
          setSearch={setSearch}
          placeholder="Search by name or client…"
          style={{ flex: 1, margin: 0 }}
        />

        <div className="view-toggle">
          <button className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('list')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            List
          </button>
          <button className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('grid')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
            Kanban
          </button>
        </div>
      </div>

      <div className={`table-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading...</p>
          </div>
        )}


        {viewType === "list" ? (
          <table className="table">
            <thead>
              <tr>
                {/* <th className="table__th" >Start Date</th> */}
                <th className="table__th">Project Name</th>
                <th className="table__th" style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isCreating && (
                <tr className="table__row table__row--editing">
                  <td className="table__td" colSpan={2}>
                    <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                      <div className="inline-form" style={{ flex: 1 }}>
                        <div className="inline-field" style={{ gridColumn: 'span 4' }}>
                          <label>Project Name</label>
                          <input className="inline-input" name="name" value={form.name} onChange={handleFieldChange} placeholder="Enter project name..." autoFocus />
                        </div>
                        <div className="inline-field">
                          <label>Budget (₹)</label>
                          <input className="inline-input-small" type="number" name="budget" value={form.budget} onChange={handleFieldChange} placeholder="0" />
                        </div>
                        <div className="inline-field">
                          <label>Start Date</label>
                          <input className="inline-input-small" type="date" name="startDate" value={form.startDate} onChange={handleFieldChange} />
                        </div>
                        <div className="inline-field">
                          <label>End Date</label>
                          <input className="inline-input-small" type="date" name="endDate" value={form.endDate} onChange={handleFieldChange} />
                        </div>
                        <div className="inline-field">
                          <label>Status</label>
                          <select className="inline-input-small" name="status" value={form.status} onChange={handleFieldChange}>
                            {CONTRACT_STATUSES.filter(s => s !== "All").map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="inline-actions" style={{ marginTop: '26px' }}>
                        <button className="btn-inline-save" onClick={handleSave} disabled={saving}>
                          {saving ? <div className="loader-small" /> : "Save"}
                        </button>
                        <button className="btn-inline-cancel" onClick={cancelEdit}>Cancel</button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {pagedData.length === 0 && !isCreating ? (
                <tr><td className="table__empty" colSpan={2}>No contracts match your filters.</td></tr>
              ) : pagedData.map((c) => (
                editingId === c.id ? (
                  <tr key={c.id} className="table__row table__row--editing">
                    <td className="table__td" colSpan={2}>
                      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                        <div className="inline-form" style={{ flex: 1 }}>
                          <div className="inline-field" style={{ gridColumn: 'span 4' }}>
                            <label>Project Name</label>
                            <input className="inline-input" name="name" value={form.name} onChange={handleFieldChange} placeholder="Enter project name..." autoFocus />
                          </div>
                          <div className="inline-field">
                            <label>Budget (₹)</label>
                            <input className="inline-input-small" type="number" name="budget" value={form.budget} onChange={handleFieldChange} placeholder="0" />
                          </div>
                          <div className="inline-field">
                            <label>Start Date</label>
                            <input className="inline-input-small" type="date" name="startDate" value={form.startDate} onChange={handleFieldChange} />
                          </div>
                          <div className="inline-field">
                            <label>End Date</label>
                            <input className="inline-input-small" type="date" name="endDate" value={form.endDate} onChange={handleFieldChange} />
                          </div>
                          <div className="inline-field">
                            <label>Status</label>
                            <select className="inline-input-small" name="status" value={form.status} onChange={handleFieldChange}>
                              {CONTRACT_STATUSES.filter(s => s !== "All").map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="inline-actions" style={{ marginTop: '26px' }}>
                          {saving && <div className="saving-indicator">Saving...</div>}
                          <button className="btn-inline-save" onClick={handleSave} disabled={saving}>
                            {saving ? <div className="loader-small" /> : "Save"}
                          </button>
                          <button className="btn-inline-cancel" onClick={cancelEdit}>Cancel</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={c.id} className="table__row table__row--clickable" onClick={() => navigate(`/contracts/${c.id}`)}>
                    <td className="table__td">
                      <div className="contract-main-info">
                        <span className="table__name">{c.name}</span>
                      </div>
                    </td>
                    <td className="table__td" style={{ textAlign: 'right' }}>
                      <button className="btn-row-edit" onClick={(e) => openEdit(e, c)} title="Edit">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        ) : (
          <div className="task-grid">
            {isCreating && (
              <div className="kanban-card kanban-card--editing">
                <div className="inline-field">
                  <label>Project Name</label>
                  <input className="inline-input" name="name" value={form.name} onChange={handleFieldChange} placeholder="Enter project name..." autoFocus />
                </div>
                <div className="inline-grid-details">
                  <div className="inline-field">
                    <label>Budget (₹)</label>
                    <input className="inline-input-small" type="number" name="budget" value={form.budget} onChange={handleFieldChange} placeholder="0" />
                  </div>
                  <div className="inline-field">
                    <label>Start Date</label>
                    <input className="inline-input-small" type="date" name="startDate" value={form.startDate} onChange={handleFieldChange} />
                  </div>
                  <div className="inline-field">
                    <label>End Date</label>
                    <input className="inline-input-small" type="date" name="endDate" value={form.endDate} onChange={handleFieldChange} />
                  </div>
                  <div className="inline-field">
                    <label>Status</label>
                    <select className="inline-input-small" name="status" value={form.status} onChange={handleFieldChange}>
                      {CONTRACT_STATUSES.filter(s => s !== "All").map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="inline-actions" style={{ marginTop: '12px' }}>
                  <button className="btn-inline-save" onClick={handleSave} disabled={saving}>
                    {saving ? <div className="loader-small" /> : "Save"}
                  </button>
                  <button className="btn-inline-cancel" onClick={cancelEdit}>Cancel</button>
                </div>
              </div>
            )}
            {pagedData.length === 0 && !isCreating ? (
              <div className="table__empty" style={{ padding: '40px' }}>No contracts match your filters.</div>
            ) : pagedData.map((c) => (
              editingId === c.id ? (
                <div key={c.id} className="kanban-card kanban-card--editing">
                  <div className="inline-field">
                    <label>Project Name</label>
                    <input className="inline-input" name="name" value={form.name} onChange={handleFieldChange} placeholder="Enter project name..." autoFocus />
                  </div>
                  <div className="inline-grid-details">
                    <div className="inline-field">
                      <label>Budget (₹)</label>
                      <input className="inline-input-small" type="number" name="budget" value={form.budget} onChange={handleFieldChange} placeholder="0" />
                    </div>
                    <div className="inline-field">
                      <label>Start Date</label>
                      <input className="inline-input-small" type="date" name="startDate" value={form.startDate} onChange={handleFieldChange} />
                    </div>
                    <div className="inline-field">
                      <label>End Date</label>
                      <input className="inline-input-small" type="date" name="endDate" value={form.endDate} onChange={handleFieldChange} />
                    </div>
                    <div className="inline-field">
                      <label>Status</label>
                      <select className="inline-input-small" name="status" value={form.status} onChange={handleFieldChange}>
                        {CONTRACT_STATUSES.filter(s => s !== "All").map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="inline-actions" style={{ marginTop: '12px' }}>
                    {saving && <div className="saving-indicator">Saving...</div>}
                    <button className="btn-inline-save" onClick={handleSave} disabled={saving}>
                      {saving ? <div className="loader-small" /> : "Save"}
                    </button>
                    <button className="btn-inline-cancel" onClick={cancelEdit}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div key={c.id} className="kanban-card" onClick={() => navigate(`/contracts/${c.id}`)}>
                  <div className="kanban-card__top">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span className="kanban-card__title">{c.name}</span>
                    </div>
                    <button className="btn-row-edit" onClick={(e) => openEdit(e, c)} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>
                </div>
              )
            ))}
          </div>

        )}

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>
    </main>

  );
}
